import { useState, useEffect, useCallback } from "react";
import { ref, set, get, onValue, update } from "firebase/database";
import { database } from "src/initialiseFirebase";
import type { MultiplayerGame, PlayerId, GameState, Side, MoveDirection } from "src/core/types";
import {
  serializeMultiplayerGame,
  deserializeMultiplayerGame,
  serializeGameState,
  createInitialMultiplayerGame
} from "src/utils/gameSerializer";
import { generateGameId, generateRoomCode } from "src/utils/generateRoomCode";
import type { GameType, Settings } from "../core/types";

type UseFirebaseGameReturn = {
  game: MultiplayerGame | null;
  loading: boolean;
  error: string | null;
  createGame: (
    playerId: PlayerId,
    gameState: GameState,
    settings: Settings,
    gameType: GameType
  ) => Promise<string | null>;
  joinGame: (roomCode: string, playerId: PlayerId) => Promise<MultiplayerGame | null>;
  sendMove: (index: number, direction: MoveDirection, side: Side) => Promise<void>;
  updateGameState: (newGameState: GameState, currentPlayer: Side) => Promise<void>;
  getPlayerGames: (playerId: PlayerId) => Promise<MultiplayerGame[]>;
  leaveGame: () => void;
};

/**
 * Main hook for managing multiplayer game state with Firebase
 */
export const useFirebaseGame = (gameId: string | null): UseFirebaseGameReturn => {
  const [game, setGame] = useState<MultiplayerGame | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Listen to game updates in real-time
  useEffect(() => {
    if (!gameId) {
      setGame(null);
      return;
    }

    setLoading(true);
    const gameRef = ref(database, `games/${gameId}`);

    const unsubscribe = onValue(
      gameRef,
      (snapshot) => {
        const data = snapshot.val();
        if (data) {
          try {
            const deserializedGame = deserializeMultiplayerGame(data);
            setGame(deserializedGame);
            setError(null);
          } catch (err) {
            console.error("Error deserializing game:", err);
            setError("Failed to load game data");
          }
        } else {
          setGame(null);
          setError("Game not found");
        }
        setLoading(false);
      },
      (err) => {
        console.error("Error listening to game:", err);
        setError("Failed to connect to game");
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [gameId]);

  /**
   * Create a new multiplayer game
   */
  const createGame = useCallback(
    async (
      playerId: PlayerId,
      gameState: GameState,
      settings: Settings,
      gameType: GameType
    ): Promise<string | null> => {
      try {
        setLoading(true);
        const newGameId = generateGameId();
        const roomCode = generateRoomCode();

        const newGame = createInitialMultiplayerGame(newGameId, roomCode, playerId, gameState, settings, gameType);

        const gameRef = ref(database, `games/${newGameId}`);
        const roomCodeRef = ref(database, `roomCodes/${roomCode}`);

        // Save game and room code mapping (critical)
        await set(gameRef, serializeMultiplayerGame(newGame));
        await set(roomCodeRef, newGameId);

        // Add to player's active games (non-critical — may fail if profile not created yet)
        try {
          const playerGameRef = ref(database, `users/${playerId}/activeGames/${newGameId}`);
          await set(playerGameRef, true);
        } catch (profileErr: unknown) {
          console.warn("Could not update active games:", profileErr instanceof Error ? profileErr.message : profileErr);
        }

        setLoading(false);
        return newGameId;
      } catch (err) {
        console.error("Error creating game:", err);
        setError("Failed to create game");
        setLoading(false);
        return null;
      }
    },
    []
  );

  /**
   * Join an existing game by room code
   */
  const joinGame = useCallback(async (roomCode: string, playerId: PlayerId): Promise<MultiplayerGame | null> => {
    try {
      setLoading(true);

      // Get game ID from room code
      const roomCodeRef = ref(database, `roomCodes/${roomCode.toUpperCase()}`);
      const roomCodeSnapshot = await get(roomCodeRef);

      if (!roomCodeSnapshot.exists()) {
        setError("Invalid room code");
        setLoading(false);
        return null;
      }

      const foundGameId = roomCodeSnapshot.val();
      const gameRef = ref(database, `games/${foundGameId}`);
      const gameSnapshot = await get(gameRef);

      if (!gameSnapshot.exists()) {
        setError("Game not found");
        setLoading(false);
        return null;
      }

      const gameData = gameSnapshot.val();
      const existingGame = deserializeMultiplayerGame(gameData);

      // Check if game already has a guest
      if (existingGame.guest) {
        setError("Game is full");
        setLoading(false);
        return null;
      }

      // Add player as guest
      const now = Date.now();
      const playerKey = `players/${playerId}`;
      await update(gameRef, {
        [playerKey]: {
          playerId,
          side: 1,
          connected: true,
          lastSeen: now
        },
        guest: playerId,
        status: "active",
        updatedAt: now
      });

      // Add to player's active games (non-critical — may fail if profile not created yet)
      try {
        const playerGameRef = ref(database, `users/${playerId}/activeGames/${foundGameId}`);
        await set(playerGameRef, true);
      } catch (profileErr: unknown) {
        console.warn("Could not update active games:", profileErr instanceof Error ? profileErr.message : profileErr);
      }

      setLoading(false);
      return { ...existingGame, guest: playerId, status: "active" };
    } catch (err) {
      console.error("Error joining game:", err);
      setError("Failed to join game");
      setLoading(false);
      return null;
    }
  }, []);

  /**
   * Send a move to Firebase
   */
  const sendMove = useCallback(
    async (index: number, direction: MoveDirection, side: Side): Promise<void> => {
      if (!gameId || !game) return;

      try {
        const now = Date.now();
        const move = {
          playerId: side === 0 ? game.host : game.guest,
          index,
          direction,
          side,
          timestamp: now
        };

        const gameRef = ref(database, `games/${gameId}`);
        const moveHistoryRef = ref(database, `games/${gameId}/moveHistory`);

        // Get current move history
        const snapshot = await get(moveHistoryRef);
        const currentHistory = snapshot.exists() ? snapshot.val() : [];

        await update(gameRef, {
          moveHistory: [...currentHistory, move],
          updatedAt: now
        });
      } catch (err) {
        console.error("Error sending move:", err);
        setError("Failed to send move");
      }
    },
    [gameId, game]
  );

  /**
   * Update the game state after a move
   */
  const updateGameState = useCallback(
    async (newGameState: GameState, currentPlayer: Side): Promise<void> => {
      if (!gameId) return;

      try {
        const gameRef = ref(database, `games/${gameId}`);
        await update(gameRef, {
          gameState: serializeGameState(newGameState),
          currentPlayer,
          updatedAt: Date.now()
        });
      } catch (err) {
        console.error("Error updating game state:", err);
        setError("Failed to update game state");
      }
    },
    [gameId]
  );

  /**
   * Get all active games for a player
   */
  const getPlayerGames = useCallback(async (playerId: PlayerId): Promise<MultiplayerGame[]> => {
    try {
      const playerGamesRef = ref(database, `users/${playerId}/activeGames`);
      const snapshot = await get(playerGamesRef);

      if (!snapshot.exists()) {
        return [];
      }

      const gameIds = Object.keys(snapshot.val());
      const games = await Promise.all(
        gameIds.map(async (gId) => {
          const gameRef = ref(database, `games/${gId}`);
          const gameSnapshot = await get(gameRef);
          if (gameSnapshot.exists()) {
            return deserializeMultiplayerGame(gameSnapshot.val());
          }
          return null;
        })
      );

      return games.filter(Boolean) as MultiplayerGame[];
    } catch (err) {
      console.error("Error fetching player games:", err);
      return [];
    }
  }, []);

  /**
   * Leave/disconnect from current game
   */
  const leaveGame = useCallback(() => {
    setGame(null);
    setError(null);
  }, []);

  return {
    game,
    loading,
    error,
    createGame,
    joinGame,
    sendMove,
    updateGameState,
    getPlayerGames,
    leaveGame
  };
};
